import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { IDimension, IGenericObject } from '@types'
import { ResponsiveSunburst } from '@nivo/sunburst'
import { Spinner, useTheme } from '@ss/meta-core-components'
import { initTheme } from '@utils'
import { CustomArcLabels } from './ArcLabel'

interface Props {
  topic: string
  data?: IGenericObject
  getData?: () => Promise<any>
  id?: string
  value?: string

  dataCy?: string
  dimensions?: IDimension
  loadingSpinner?: JSX.Element
}

/**
 * Componente custom per un grafico di tipo Sumburst
 * @param props.data {(IGenericObject<string | number>)=}
 * @param props.getData {Function=}
 * @param props.topic {string} topic del grafico usato per il titolo e per la richiesta rest
 * @param props.id {string=} nome della chiave del dato usato per popolare il grafico
 * @param props.value {string=} nome del campo contenente il dato usato per popolare il grafico
 * @param props.dimensions {IDimension= } dimensioni del container
 * @param props.dataCy {string=} support test e2e
 * @param props.loadingSpinner {JSX.Element=} spinner personalizzato
 * @component
 */
export const Sunburst: React.FC<Props> = (props) => {
  const {
    data,
    getData,
    topic,
    id = 'name',
    value = 'loc',
    dataCy = '',
    dimensions = { height: '375px', width: '100%' },
    loadingSpinner,
  } = props

  const { type } = useTheme()
  const [chartProps, setChartProps] = useState<{
    data: any
    [key: string]: any
  }>()

  useEffect(() => {
    const newProps = {
      data: data || [],
      margin: { top: 20, right: 20, bottom: 20, left: 20 },
      id,
      value,
      cornerRadius: 2,
      borderColor: { theme: 'background' },
      enableArcLabels: true,
      innerRadius: 0.1,
      arcLabel: (d: { id: string; path: string[] }) => {
        if (d.path.length === 2) return `${d.id}`
        return ''
      },
      arcLabelsSkipAngle: 15,
      animate: true,
      arcLabelsTextColor: {
        from: 'color',
        modifiers: [['darker', 1.4]],
      },
    }
    if (getData) {
      getData().then((res) => {
        newProps.data = res.data
        setChartProps(newProps)
      })
      return
    }
    setChartProps(newProps)
  }, [data])

  return (
    <Container
      height={dimensions.height || '375px'}
      width={dimensions.width || '100%'}
      data-cy={`${dataCy}-sunburst`}
      loading={false}
    >
      {chartProps?.data ? (
        <ResponsiveSunburst
          {...chartProps}
          layers={['arcs', CustomArcLabels]}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]],
          }}
          theme={initTheme(type)}
        />
      ) : (
        <Loading>
          <Loading>
            {loadingSpinner ? (
              loadingSpinner
            ) : (
              <Spinner data-cy={`${dataCy}-sunburst-loading`} />
            )}
          </Loading>
          <Loading>LOADING</Loading>
        </Loading>
      )}
    </Container>
  )
}

const Container = styled.div<{
  height: string
  width: string
  loading?: boolean
}>`
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  ${({ loading }) => loading && 'display:grid'};
  transition: width 0.2s ease-in-out, height 0.2s ease-in-out;
`
const Loading = styled.div`
  display: grid;
  place-self: center;
  gap: 10px;
  color: cyan;
`
